import Badge from '@pelckmans/elementis/components/badge';
import Button from '@pelckmans/elementis/components/button';
import Section from '@pelckmans/elementis/components/section';
import SelectField from '@pelckmans/elementis/components/select-field';
import FilterOffIcon from '@pelckmans/elementis/icons/filter-off';
import FilterOnIcon from '@pelckmans/elementis/icons/filter-on';
import { useCallback, useContext, useId, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../api/groups';
import ModalContext from '../../../../../contexts/ModalContext';
import LocalizedDateField, {
  dateFormatter,
} from '../../../../../shared-components/elementis/components/localized-date-field';
import LoadingState from '../../../../../shared-components/LoadingState';
import ModuleContext from '../../../../material/_contexts/ModuleContext';
import { SchoolYearValue } from '../../../_hooks/useSchoolYearValues';
import FilterStudentsModal from '../../../../../shared-components/filter-students-modal/FilterStudentsModal';
import { useMembers } from '../../../results/context/ResultsDataContext';
import TeacherAssignmentsContext, { TeacherAssignmentsContextProvider } from '../contexts/TeacherAssignmentsContext';
import useAssignmentFilters from '../hooks/useAssignmentFilters';
import AssignmentsTable from './AssignmentsTable';
import GroupsEmptyState from './GroupsEmptyState';
import NoAssignmentsState from './NoAssignmentsState';
import NoLicenseState from './NoLicenseState';
import useModuleAccess from '../../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../../core/access/module-access';
import { sortByLastNameFirstName } from '../../../../../utils/sortByLastNameFirstName';

const BASE = 'PAGES.ASSIGNMENTS.TEACHER.SECTION';
const SIZE_FILTERS = 'sm';

interface Props {
  isLoadingGroups: boolean;
  groups: Group[];
  archived?: boolean;
  schoolYear?: SchoolYearValue;
  onSchoolYearChange?: (schoolYear: SchoolYearValue) => void;
}

function Assignments({ isLoadingGroups, groups }: Omit<Props, 'archived'>) {
  const { t } = useTranslation();
  const { module } = useContext(ModuleContext);
  const { assignmentsAccess } = useModuleAccess(module);
  const {
    assignments,
    isLoading: isLoadingAssignments,
    archived,
    schoolYears,
    selectedSchoolYear,
    setSelectedSchoolYear,
  } = useContext(TeacherAssignmentsContext);
  const { showModal } = useContext(ModalContext);
  const id = useId();
  const today = useMemo(() => new Date(), []);

  const isLoading = isLoadingGroups || isLoadingAssignments;

  const { members } = useMembers(groups);
  // Only the current groups, useMembers keeps members of groups from a previously selected year
  const membersOfSelectedYear = useMemo(
    () =>
      Object.fromEntries(groups.map(group => [group.id, [...(members[group.id] ?? [])].sort(sortByLastNameFirstName)])),
    [groups, members],
  );

  const [dateRangeControl, setDateRangeControl] = useState<DateRange | undefined>();
  const filterGroups = useMemo(
    () =>
      groups.map(group => ({
        id: group.id,
        name: group.name,
        acceptedMemberCount: membersOfSelectedYear[group.id]?.length ?? 0,
      })),
    [groups, membersOfSelectedYear],
  );

  const [selectedMemberIdsPerGroup, setSelectedMemberIdsPerGroup] = useState<Record<string, string[]>>({});
  const selectedMembersPerGroup = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(selectedMemberIdsPerGroup).map(([groupId, memberIds]) => [
          groupId,
          (membersOfSelectedYear[groupId] ?? []).filter(member => memberIds.includes(member.id)),
        ]),
      ),
    [selectedMemberIdsPerGroup, membersOfSelectedYear],
  );

  const { filteredAssignments, selectionCount } = useAssignmentFilters({
    assignments,
    selectedMemberIdsPerGroup,
    dateRange: dateRangeControl,
    archived,
  });

  const clearFilters = useCallback(() => {
    setDateRangeControl(undefined);
    setSelectedMemberIdsPerGroup({});
  }, []);

  const handleSchoolYearChange = useCallback(
    (schoolYear: SchoolYearValue) => {
      setSelectedSchoolYear(schoolYear);
      clearFilters();
    },
    [setSelectedSchoolYear, clearFilters],
  );

  const clearStudentsFilterLabel = t(`${BASE}.FILTERS.FILTER_STUDENTS.CLEAR_FILTER_BUTTON.A11Y_LABEL`);

  const userHasIncorrectLicense = assignmentsAccess === FEATURE_ACCESS.LOCKED;
  const zeroGroups = !isLoadingGroups && groups.length === 0;
  const zeroAssignments = !isLoadingAssignments && assignments.length === 0;
  const zeroMatchingAssignments = !isLoadingAssignments && assignments.length > 0 && filteredAssignments.length === 0;

  let content;
  let showFilters = false;

  if (isLoading) {
    content = <LoadingState variant="table" aria-label={t(`${BASE}.TABLE.EMPTY_STATE.A11Y_LABEL`)} />;
  } else if (zeroGroups) {
    content = <GroupsEmptyState id={id} />;
  } else if (userHasIncorrectLicense) {
    content = <NoLicenseState id={id} />;
  } else if (zeroAssignments) {
    content = <NoAssignmentsState archived={archived} id={id} />;
    showFilters = true;
  } else if (zeroMatchingAssignments) {
    content = <NoAssignmentsState archived={archived} id={id} filtered onClearFilters={clearFilters} />;
    showFilters = true;
  } else {
    content = <AssignmentsTable assignments={filteredAssignments} archived={archived} id={id} />;
    showFilters = true;
  }

  const filterProps = useMemo(() => {
    if (!showFilters) return {};

    function renderDateRangeValue(range: DateRange) {
      return t(`${BASE}.FILTERS.FILTER_RANGE.DATE_FIELD.VALUE`, {
        from: dateFormatter.format(range.from),
        to: range.to ? dateFormatter.format(range.to) : '...',
      });
    }

    const isCurrentSchoolYearSelected = selectedSchoolYear?.id === schoolYears?.current.id;
    const defaultMonth = dateRangeControl || isCurrentSchoolYearSelected ? undefined : selectedSchoolYear?.from;
    // Planning a past date is pointless, but the archive is past by definition
    const minDate = !archived && isCurrentSchoolYearSelected ? today : selectedSchoolYear?.from;

    const schoolYearOptions = [schoolYears?.current, schoolYears?.previous].filter((year): year is SchoolYearValue =>
      Boolean(year),
    );

    return {
      filter: (
        <>
          <SelectField
            value={selectedSchoolYear || null}
            onChange={handleSchoolYearChange}
            required
            // The value and the items come from different hook instances, so identity comparison fails
            matchOn="id"
            size={SIZE_FILTERS}
            aria-label={t(`${BASE}.FILTERS.FILTER_SCHOOL_YEAR.SELECT_FIELD.A11Y_LABEL`)}
            renderValueContent={year => year.title}
            dropdownMenuProps={{
              ariaLabel: t(`${BASE}.FILTERS.FILTER_SCHOOL_YEAR.SELECT_FIELD.DROPDOWN_MENU.A11Y_LABEL`),
            }}
            readOnly={!archived}
          >
            {schoolYearOptions.map(year => (
              <SelectField.Item key={year.id} text={year.title} value={year} />
            ))}
          </SelectField>
          <LocalizedDateField
            required
            mode="range"
            selected={dateRangeControl}
            onSelect={setDateRangeControl}
            aria-label={t(`${BASE}.FILTERS.FILTER_RANGE.DATE_FIELD.A11Y_LABEL`)}
            placeholder={t(`${BASE}.FILTERS.FILTER_RANGE.${archived ? 'ARCHIVED' : 'PLANNED'}.DATE_FIELD.PLACEHOLDER`)}
            dateMenuAriaLabel={t(`${BASE}.FILTERS.FILTER_RANGE.DATE_FIELD.DATE_MENU.A11Y_LABEL`)}
            renderValue={renderDateRangeValue}
            size={SIZE_FILTERS}
            className="select-field"
            minDate={minDate}
            maxDate={selectedSchoolYear?.to}
            defaultMonth={defaultMonth}
          />
        </>
      ),
      filterActions: (
        <>
          <Button
            startIcon={<FilterOnIcon />}
            size={SIZE_FILTERS}
            prominence="neutral"
            onClick={() => {
              showModal(
                <FilterStudentsModal
                  id="filter-students-modal"
                  translationKeyBase={`${BASE}.FILTERS.FILTER_STUDENTS.MODAL`}
                  groups={filterGroups}
                  selectedGroups={filterGroups}
                  membersPerGroup={membersOfSelectedYear}
                  selectedMembersPerGroup={selectedMembersPerGroup}
                  setSelectedMembers={setSelectedMemberIdsPerGroup}
                />,
              );
            }}
            badge={
              selectionCount > 0 ? (
                <Badge prominence="ghost" size="sm">
                  {selectionCount}
                </Badge>
              ) : undefined
            }
            key="open-filter-students-modal"
          >
            {t(`${BASE}.FILTERS.FILTER_STUDENTS.OPEN_MODAL_BUTTON`)}
          </Button>
          {selectionCount > 0 && (
            <Button
              startIcon={<FilterOffIcon data-testid="filter-off-icon" />}
              aria-label={clearStudentsFilterLabel}
              tooltip={clearStudentsFilterLabel}
              onClick={() => setSelectedMemberIdsPerGroup({})}
              key="clear-students-filter"
              prominence="neutral"
              size={SIZE_FILTERS}
            />
          )}
        </>
      ),
      filterActionsGroupRole: 'group' as const,
      filterActionsAriaLabel: t(`${BASE}.FILTERS.FILTER_STUDENTS.BUTTON_GROUP_A11Y_LABEL`),
      filterMobileButtonTitle: t(`${BASE}.FILTERS.FILTER_STUDENTS.MOBILE_BUTTON.TITLE`),
      filterMobileButtonActiveTitle: t(`${BASE}.FILTERS.FILTER_STUDENTS.MOBILE_BUTTON.ACTIVE_TITLE`),
    };
  }, [
    showFilters,
    archived,
    selectedSchoolYear,
    handleSchoolYearChange,
    t,
    schoolYears,
    today,
    dateRangeControl,
    selectionCount,
    filterGroups,
    selectedMembersPerGroup,
    membersOfSelectedYear,
    clearStudentsFilterLabel,
    showModal,
  ]);

  return (
    <Section
      sectionLabelId={id}
      title={t(`${BASE}.${archived ? 'TITLE_ARCHIVED' : 'TITLE'}`)}
      className="teacher-assignments"
      contentClassName="content-wrapper"
      {...filterProps}
    >
      {content}
    </Section>
  );
}

function WrappedAssignments({ archived, schoolYear, onSchoolYearChange, ...props }: Props) {
  return (
    <TeacherAssignmentsContextProvider
      archived={archived ?? false}
      schoolYear={schoolYear}
      onSchoolYearChange={onSchoolYearChange}
    >
      <Assignments {...props} />
    </TeacherAssignmentsContextProvider>
  );
}

export default WrappedAssignments;
